'use client';

import { Box, Theme } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';

// A coloured tile sitting behind the state icon. `shape` picks between the
// squarish "ready" tile (radius `lg`) and the circular status tiles used by
// every other state (radius `round`), per the design -> theme token mapping.
//
// Wrapped in withClarityTheme below: every background/iconColor call site reaches for a
// surface.*Subtle token that only exists on the Clarity theme, and this component has no
// control over what theme a future caller renders it under. Nesting withClarityTheme inside an
// already Clarity-themed screen is idempotent (the HOC re-derives the same Clarity theme from
// the ambient light/dark mode), so this costs nothing for today's callers and fixes the silent
// no-background render for any caller outside a Clarity tree.
export type StatusIconTileProps = Readonly<{
  background: (theme: Theme) => string;
  // A theme callback, the same as `background`, not a bare string: a bare dot-path string
  // (for example 'primary.main') that fails to resolve is silently treated by MUI as a
  // literal CSS value, so a typo would compile and render with no colour at all. Routing
  // through the theme instead forces the caller's string to actually be a valid theme path.
  iconColor: (theme: Theme) => string;
  shape: 'lg' | 'round';
  // A single icon element, not ReactNode: the styling below hardcodes a rule for one direct
  // `<svg>` child, so text, a fragment, or more than one icon would compile but render wrong.
  icon: ReactElement;
  'data-testid'?: string;
}>;

function StatusIconTileComponent({
  background,
  iconColor,
  shape,
  icon,
  'data-testid': dataTestId,
}: StatusIconTileProps): ReactElement {
  return (
    <Box
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        width: theme.semantic.size['4xs'],
        height: theme.semantic.size['4xs'],
        borderRadius:
          shape === 'round' ? theme.semantic.borderRadius.round : theme.semantic.borderRadius.lg,
        backgroundColor: background(theme),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: iconColor(theme),
        '& > svg': { fontSize: theme.semantic.size['6xs'] },
      })}
    >
      {icon}
    </Box>
  );
}

export const StatusIconTile = withClarityTheme(StatusIconTileComponent);
